// =============================================================================
// Dérivation des libellés / sélections par PROFIL MÉTIER (business_model)
// -----------------------------------------------------------------------------
// FONDATION (lot 1.0) du plan PLAN-IMPL-DERIVATION-PROFIL-AMO-INSTALLATEUR.
// Objectif : UN helper unique pour dériver libellés + sélections du profil métier,
// au lieu de relabels en dur dupliqués ou de gates par slug ("omv_france"/"tef").
//
// Source canonique = tenants.config.business_model, exposé côté front par
// utils/tenantProfile.js:getBusinessModel() ("amo" | "installateur" | "").
// Climat (futur installateur) hérite GRATUITEMENT en posant son business_model.
//
// ⚠️ ANTI "fail-to-TEF" dès la fondation : aucun slug tenant en dur ici ; un
// business_model inconnu/absent → défaut NEUTRE ("" pour les libellés, [] pour
// les sélections), JAMAIS un repli silencieux vers AMO/TEF.
//
// NB lot 1.0 : on POSE le socle + les tests. Les écrans réels NE sont PAS encore
// rebranchés (1.1→1.6 viendront après validation du pattern). Additif pur.
// =============================================================================

import { getBusinessModel, getRegimeAides } from "./tenantProfile";

export const AMO = "amo";
export const INSTALLATEUR = "installateur";
const PROFILES = [AMO, INSTALLATEUR];

// Table des libellés dérivés par profil. Extensible : ajouter une clé avec sa
// valeur pour chaque profil. Clé absente d'un profil → "" (neutre).
const LABELS = {
  [AMO]: {
    visiteur: "Visiteur",
    aides_menu: "Aides",
    dashboard_kpi_principal: "Contrats AMO",
    devis_numero: "N° devis",
  },
  [INSTALLATEUR]: {
    visiteur: "Poseur",
    aides_menu: "CEE",
    dashboard_kpi_principal: "Devis CEE",
    devis_numero: "N° devis",
  },
};

// Sélections (listes) dérivées par profil. Squelettes : API + défaut sûr, PAS
// encore branchés sur les écrans réels (DocumentsForm, HubGrid, Dashboard…).
const DOC_TYPES = {
  [AMO]: ["mandat_amo", "facture_amo", "dal", "plan_financement"],
  [INSTALLATEUR]: ["devis_cee", "facture_travaux", "attestation_rge"],
};
const HUB_COLUMNS = {
  [AMO]: ["reference", "client", "statut", "visiteur", "montant_amo"],
  [INSTALLATEUR]: ["reference", "beneficiaire", "etablissement", "cumac", "prime"],
};
const DEFAULT_WIDGETS = {
  [AMO]: ["monthly_amo", "pipeline_anah", "conformite", "finance"],
  [INSTALLATEUR]: ["devis_cee", "prime_cee", "reste_a_charge"],
};

// Profil métier courant, NORMALISÉ. Toute valeur hors {amo, installateur}
// (inconnue, vide, slug…) → "" (neutre). Jamais de slug ici.
export function currentProfile() {
  const m = getBusinessModel();
  return PROFILES.includes(m) ? m : "";
}

// label(key[, model]) → libellé dérivé du profil (model = profil courant par défaut).
// Profil inconnu OU clé absente → "" (neutre, jamais un défaut AMO/TEF caché).
export function label(key, model = currentProfile()) {
  const table = LABELS[model];
  return (table && table[key]) || "";
}

// --- Sélecteurs dérivés (défaut sûr [] si profil inconnu — anti fail-to-TEF) ---
// Renvoient une COPIE pour que l'appelant ne puisse pas muter la table source.
export function getDocTypes(model = currentProfile()) {
  return DOC_TYPES[model] ? [...DOC_TYPES[model]] : [];
}
export function getHubColumns(model = currentProfile()) {
  return HUB_COLUMNS[model] ? [...HUB_COLUMNS[model]] : [];
}
export function getDefaultWidgets(model = currentProfile()) {
  return DEFAULT_WIDGETS[model] ? [...DEFAULT_WIDGETS[model]] : [];
}

// =============================================================================
// GATING / LIBELLÉS « MPR » — dérivés du RÉGIME D'AIDES (tenants.config.regime_aides)
// -----------------------------------------------------------------------------
// Un profil « touche au MPR » si son régime contient "mpr" (MaPrimeRénov')
// OU "anah" (workflow ANAH / MaPrimeAdapt). SEUL un profil CEE-only (ex. OMV :
// regime=["cee"]) n'affiche AUCUN MPR. ⚠️ Prédicat UNIQUE, dérivé du régime,
// JAMAIS d'un slug isOmv/tef — tout futur tenant hérite automatiquement.
//   tef=["anah"]→true · climatpac=["mpr","cee"]→true · omv_france=["cee"]→false
// =============================================================================
export function regimeShowsMpr(regime) {
  const r = new Set(regime || []);
  return r.has("mpr") || r.has("anah");
}

// Prédicat du tenant courant (lit le régime en localStorage). Défaut sûr : régime
// absent → false (pas de MPR affiché) — anti fail-to-TEF.
export function currentShowsMpr() {
  return regimeShowsMpr(getRegimeAides());
}

// Libellés dérivés de la présence MPR (showMpr booléen). Profil CEE-only → variante
// sans MPR. Centralise les chaînes (relabels #1/#8/#2/#4).
const AIDES_MPR_LABELS = {
  aides_menu:            { yes: "Aides (MPR/CEE)",                no: "Aides (CEE)" },
  aides_sous_titre:      { yes: "Simulateur, CEE & MaPrimeRénov", no: "Simulateur & CEE" },
  aides_mentions_header: { yes: "aides MPR et CEE",               no: "aides CEE" },
};

export function aidesLabel(key, showMpr) {
  const entry = AIDES_MPR_LABELS[key];
  if (!entry) return "";
  return showMpr ? entry.yes : entry.no;
}
